import { computed, ref, watch } from 'vue';

import logger from '@/services/logger';
import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import { carriedTypes, clampCell, eligibleFor, evaluate, fillForward, isListed, maxEvenWeeks, pluralWeeks, spreadEvenly, tightestType } from '@/utils/splitAcrossWeeks';
import type { SplitCells, SplitEvaluation, SplitPreset, SplitType, SplitWeek } from '@/utils/splitAcrossWeeks';

export type SaveState = 'waiting' | 'saving' | 'saved' | 'failed';

export interface SplitInputs {
  vendorId: string;
  poNumber: string | null;
  notes: string;
  types: SplitType[];
  weeks: SplitWeek[];
  // The calendar row (and its capacity unit) behind each type, so the pane can create a
  // missing week plan the same way the board's "No plan" cell does.
  rowIdByType: Record<string, string>;
  unitByType: Record<string, string | null>;
}

export interface WeekCardLine {
  type: SplitType;
  active: boolean;
  reason: string | null;
  value: number;
  left: number | null;
  over: boolean;
  edited: boolean;
  // Non-null only where creating a plan would make this line live: the order carries the type
  // but the week has no plan for it. A type the order does not carry never offers the action.
  rowId: string | null;
  unit: string | null;
}

export interface WeekCardModel {
  week: SplitWeek;
  kind: 'card' | 'skipped' | 'planned';
  plannedSummary: string | null;
  lines: WeekCardLine[];
  overTypes: SplitType[];
  saveState: SaveState | null;
  isFirst: boolean;
  tag: string;
}

export interface ConsequenceLine {
  tone: 'ok' | 'warn' | 'bad';
  text: string;
}

export type FooterState =
  | { kind: 'saving'; index: number; total: number }
  | { kind: 'halted'; label: string; parts: string[] }
  | { kind: 'over'; weeks: { label: string; parts: string[] }[] }
  | { kind: 'open'; parts: string[] }
  | { kind: 'complete'; parts: string[]; weeks: number };

// The stepper's starting point when the split opens or the first week changes — not a floor;
// evenN can go as low as 1 when fewer weeks are eligible (see maxEven clamping below).
const DEFAULT_EVEN_WEEKS = 2;
const MORE_WEEKS_STEP = 6;

const fmt = (value: number): string => value.toLocaleString();

export function useSplitAcrossWeeks(inputs: () => SplitInputs | null, onSaved: () => void) {
  const calendarStore = useCapacityCalendarStore();

  const preset = ref<SplitPreset>('fill');
  // maxEven may not be known yet (inputs can start null), so this starts at the plain default
  // and is reconciled against maxEven lazily, in applyPreset()/reset() below.
  const evenN = ref(DEFAULT_EVEN_WEEKS);
  const cells = ref<SplitCells>({});
  const edited = ref<Record<string, Set<string>>>({});
  const extraWeeks = ref(0);
  const saving = ref(false);
  const halted = ref(false);
  const saveStates = ref<Record<string, SaveState>>({});
  // The weeks as they stood when the save started. Every saved week patches the store's
  // remaining capacity, which would otherwise repaint the cell that just wrote it as
  // over-booked against its own quantity.
  const frozenWeeks = ref<SplitWeek[] | null>(null);

  const current = computed(() => inputs());
  const types = computed(() => current.value?.types ?? []);
  const isReservation = computed(() => current.value?.poNumber === null);
  // While a save is in flight or halted on a failed week, the plan is frozen: only retry() or
  // reset() can move it forward again.
  const locked = computed(() => saving.value || halted.value);
  const weeks = computed<SplitWeek[]>(() => (locked.value && frozenWeeks.value ? frozenWeeks.value : (current.value?.weeks ?? [])));

  const evaluation = computed<SplitEvaluation>(() => evaluate(types.value, weeks.value, cells.value));
  const maxEven = computed(() => maxEvenWeeks(types.value, weeks.value, isReservation.value));

  const fillPreview = computed(() => evaluate(types.value, weeks.value, fillForward(types.value, weeks.value, isReservation.value)));
  const evenPreview = computed(() => evaluate(types.value, weeks.value, spreadEvenly(types.value, weeks.value, evenN.value, isReservation.value)));

  // The stepper's default for the current weeks: however many weeks fill actually needs (spec:
  // "defaults to the fill's week count, at least 2"), never above maxEven, and the floor itself
  // drops to maxEven when fewer weeks are eligible (down to 1, or 1 when there are none at all).
  function defaultEvenN(): number {
    const fillWeekCount = evaluate(types.value, weeks.value, fillForward(types.value, weeks.value, isReservation.value)).plannedWeekKeys.length;
    const lowerBound = Math.max(1, Math.min(DEFAULT_EVEN_WEEKS, maxEven.value));
    return Math.max(lowerBound, Math.min(maxEven.value, fillWeekCount));
  }

  // The eligible set can grow or shrink under an open split (a plan created from the pane, or
  // removed) — a user-set stepper value stops meaning anything once the weeks it counted over
  // have changed, so it re-derives to the same default fill would pick. Presets stay one-shot:
  // this only moves evenN, it never re-applies a preset or touches cells.
  watch(maxEven, () => {
    if (locked.value) return;
    evenN.value = defaultEvenN();
  });

  function labelOf(weekKey: string | null): string {
    return weeks.value.find((week) => week.weekKey === weekKey)?.label ?? '';
  }

  function codeOf(capacityTypeId: string): string {
    return types.value.find((type) => type.capacityTypeId === capacityTypeId)?.code ?? capacityTypeId;
  }

  const fillLine = computed<ConsequenceLine>(() => {
    const preview = fillPreview.value;
    const open = carriedTypes(types.value).filter((type) => preview.openByType[type.capacityTypeId] > 0);
    if (open.length > 0) {
      return {
        tone: 'warn',
        text: `${pluralWeeks(preview.plannedWeekKeys.length)} · ${open.map((type) => `${fmt(preview.openByType[type.capacityTypeId])} ${type.code}`).join(', ')} would not fit in the loaded weeks`,
      };
    }
    return { tone: 'ok', text: `${pluralWeeks(preview.plannedWeekKeys.length)} · done by ${labelOf(preview.lastWeekKey)} · nothing over capacity` };
  });

  const evenLine = computed<ConsequenceLine>(() => {
    const preview = evenPreview.value;
    const overWeeks = new Set(preview.over.map((entry) => entry.weekKey)).size;
    if (overWeeks > 0) return { tone: 'bad', text: `${pluralWeeks(preview.plannedWeekKeys.length)} · ${overWeeks} would be over capacity` };
    return { tone: 'ok', text: `${pluralWeeks(preview.plannedWeekKeys.length)} · nothing over capacity` };
  });

  const problem = computed(() => {
    const first = weeks.value[0];
    if (!first) return '';
    const tightest = tightestType(types.value, first);
    if (!tightest) return `${first.label} has no plan for this order. Choose how to place it in the following weeks.`;
    const fits = Math.max(0, first.remainingByType[tightest.capacityTypeId] ?? 0);
    if (fits >= tightest.total) return `${first.label} can take the whole order. Split it anyway, then change any week you like.`;
    const noun = isReservation.value ? 'the reservation' : 'the order';
    return `${first.label} has room for ${fmt(fits)} of the ${fmt(tightest.total)} ${tightest.code}. Choose how to place ${noun}, then change any week you like.`;
  });

  // First week through the last week holding a value, plus the next week any type can use,
  // plus whatever "Show later weeks" appended.
  const listedWeeks = computed<SplitWeek[]>(() => {
    const all = weeks.value;
    const lastKey = evaluation.value.lastWeekKey;
    const lastIndex = lastKey ? all.findIndex((week) => week.weekKey === lastKey) : -1;
    let end = lastIndex + 1;
    const nextEligible = all.slice(end).findIndex((week) => isListed(week, types.value, isReservation.value));
    if (nextEligible >= 0) end += nextEligible + 1;
    end += extraWeeks.value;
    return all.slice(0, Math.max(end, 1));
  });

  function rowIdFor(capacityTypeId: string): string | null {
    return current.value?.rowIdByType[capacityTypeId] ?? null;
  }

  function unitFor(capacityTypeId: string): string | null {
    return current.value?.unitByType[capacityTypeId] ?? null;
  }

  const cards = computed<WeekCardModel[]>(() =>
    listedWeeks.value.map((week, index) => {
      const saveState = saveStates.value[week.weekKey] ?? null;
      if (!isReservation.value && week.alreadyScheduled) {
        const summary = carriedTypes(types.value)
          .map((type) => {
            const mine = (week.bookedByType[type.capacityTypeId] ?? []).filter((booking) => booking.poNumber === current.value?.poNumber);
            return mine.length > 0 ? `${type.code} ${fmt(mine.reduce((sum, booking) => sum + booking.quantity, 0))}` : null;
          })
          .filter((part): part is string => part !== null)
          .join(' · ');
        return { week, kind: 'planned', plannedSummary: summary, lines: [], overTypes: [], saveState: null, isFirst: index === 0, tag: '' };
      }
      const lines: WeekCardLine[] = types.value.map((type) => {
        const carried = type.total > 0;
        const remaining = week.remainingByType[type.capacityTypeId];
        const active = carried && typeof remaining === 'number';
        const value = cells.value[week.weekKey]?.[type.capacityTypeId] ?? 0;
        const left = active ? (remaining as number) - value : null;
        const reason = active ? null : !carried ? (isReservation.value ? 'not in this reservation' : 'not in this order') : `no ${type.code} plan this week`;
        return {
          type,
          active,
          reason,
          value,
          left,
          over: value > 0 && left !== null && left < 0,
          edited: edited.value[week.weekKey]?.has(type.capacityTypeId) ?? false,
          rowId: !active && carried ? rowIdFor(type.capacityTypeId) : null,
          unit: unitFor(type.capacityTypeId),
        };
      });
      const overTypes = lines.filter((line) => line.over).map((line) => line.type);
      const editedCodes = lines.filter((line) => line.edited).map((line) => line.type.code);
      const tag = index === 0 ? 'the week you picked' : editedCodes.length > 0 ? `${editedCodes.join(', ')} changed by hand` : '';
      // A week no carried type can use gets the same lines as an active card — every line is
      // inactive, so the card shows "add capacity" wherever a bar would be.
      const kind: WeekCardModel['kind'] = isListed(week, types.value, isReservation.value) ? 'card' : 'skipped';
      return { week, kind, plannedSummary: null, lines, overTypes, saveState, isFirst: index === 0, tag };
    })
  );

  const placedTotal = computed(() => Object.values(evaluation.value.placedByType).reduce((sum, value) => sum + value, 0));

  const footer = computed<FooterState>(() => {
    const ev = evaluation.value;
    if (saving.value) {
      const done = ev.plannedWeekKeys.filter((weekKey) => saveStates.value[weekKey] === 'saved').length;
      return { kind: 'saving', index: done, total: ev.plannedWeekKeys.length };
    }
    if (halted.value) {
      const failedKey = ev.plannedWeekKeys.find((weekKey) => saveStates.value[weekKey] === 'failed') ?? null;
      const parts = carriedTypes(types.value)
        .map((type) => ({ code: type.code, quantity: failedKey ? (cells.value[failedKey]?.[type.capacityTypeId] ?? 0) : 0 }))
        .filter((part) => part.quantity > 0)
        .map((part) => `${fmt(part.quantity)} ${part.code}`);
      return { kind: 'halted', label: labelOf(failedKey), parts };
    }
    if (ev.over.length > 0) {
      const byWeek = new Map<string, string[]>();
      ev.over.forEach((entry) => {
        byWeek.set(entry.weekKey, [...(byWeek.get(entry.weekKey) ?? []), `${codeOf(entry.capacityTypeId)} ${fmt(entry.by)}`]);
      });
      return { kind: 'over', weeks: [...byWeek.entries()].map(([weekKey, parts]) => ({ label: labelOf(weekKey), parts })) };
    }
    const open = carriedTypes(types.value).filter((type) => ev.openByType[type.capacityTypeId] > 0);
    if (open.length > 0) return { kind: 'open', parts: open.map((type) => `${fmt(ev.openByType[type.capacityTypeId])} ${type.code}`) };
    return { kind: 'complete', parts: carriedTypes(types.value).map((type) => `${fmt(ev.placedByType[type.capacityTypeId])} ${type.code}`), weeks: ev.plannedWeekKeys.length };
  });

  function applyPreset(next: SplitPreset): void {
    if (locked.value) return;
    preset.value = next;
    if (next === 'fill') {
      cells.value = fillForward(types.value, weeks.value, isReservation.value);
      evenN.value = defaultEvenN();
    } else {
      cells.value = spreadEvenly(types.value, weeks.value, evenN.value, isReservation.value);
    }
    edited.value = {};
    extraWeeks.value = 0;
    halted.value = false;
    saveStates.value = {};
  }

  function setEvenN(n: number): void {
    if (locked.value) return;
    evenN.value = Math.max(1, Math.min(maxEven.value, Math.round(n)));
    if (preset.value === 'even') applyPreset('even');
  }

  function setCell(weekKey: string, capacityTypeId: string, raw: number): void {
    if (locked.value) return;
    const value = clampCell(types.value, cells.value, weekKey, capacityTypeId, raw);
    cells.value = { ...cells.value, [weekKey]: { ...(cells.value[weekKey] ?? {}), [capacityTypeId]: value } };
    edited.value = { ...edited.value, [weekKey]: new Set([...(edited.value[weekKey] ?? []), capacityTypeId]) };
  }

  // Places what is open into the next week after the last used one, capped per type. A type
  // with nothing open is skipped entirely so a no-op click never marks that cell hand-edited.
  function addWeek(): void {
    if (locked.value) return;
    const ev = evaluation.value;
    const all = weeks.value;
    const lastIndex = ev.lastWeekKey ? all.findIndex((week) => week.weekKey === ev.lastWeekKey) : -1;
    const next = all.slice(lastIndex + 1).find((week) => isListed(week, types.value, isReservation.value));
    if (!next) return;
    carriedTypes(types.value).forEach((type) => {
      const open = ev.openByType[type.capacityTypeId];
      if (open <= 0 || !eligibleFor(next, type, isReservation.value)) return;
      const room = Math.max(0, next.remainingByType[type.capacityTypeId] ?? 0);
      const quantity = Math.min(room, open);
      if (quantity <= 0) return;
      setCell(next.weekKey, type.capacityTypeId, quantity);
    });
  }

  // Tops up every week with room, in order, until nothing is open. Existing cells stay.
  function fillOpen(): void {
    if (locked.value) return;
    const ev = evaluation.value;
    const open = { ...ev.openByType };
    weeks.value.forEach((week) => {
      carriedTypes(types.value).forEach((type) => {
        if (open[type.capacityTypeId] <= 0 || !eligibleFor(week, type, isReservation.value)) return;
        const currentValue = cells.value[week.weekKey]?.[type.capacityTypeId] ?? 0;
        const room = Math.max(0, (week.remainingByType[type.capacityTypeId] ?? 0) - currentValue);
        const quantity = Math.min(room, open[type.capacityTypeId]);
        if (quantity <= 0) return;
        setCell(week.weekKey, type.capacityTypeId, currentValue + quantity);
        open[type.capacityTypeId] -= quantity;
      });
    });
  }

  function showMoreWeeks(): void {
    extraWeeks.value += MORE_WEEKS_STEP;
  }

  async function runFrom(startIndex: number): Promise<void> {
    const input = current.value;
    if (!input) return;
    const order = evaluation.value.plannedWeekKeys;
    saving.value = true;
    halted.value = false;
    try {
      for (const weekKey of order.slice(startIndex)) {
        saveStates.value = { ...saveStates.value, [weekKey]: 'saving' };
        const portions = carriedTypes(types.value)
          .map((type) => ({ capacityTypeId: type.capacityTypeId, quantity: cells.value[weekKey]?.[type.capacityTypeId] ?? 0 }))
          .filter((portion) => portion.quantity > 0);
        const ok = await calendarStore.distributePurchaseOrder(
          input.vendorId,
          weekKey,
          input.poNumber,
          portions,
          input.notes.trim().length > 0 ? input.notes.trim() : undefined,
          undefined,
          { quiet: true }
        );
        if (!ok) {
          saveStates.value = { ...saveStates.value, [weekKey]: 'failed' };
          halted.value = true;
          return;
        }
        saveStates.value = { ...saveStates.value, [weekKey]: 'saved' };
      }
      if (input.poNumber) calendarStore.setRecentAssignments([input.poNumber], order);
      logger.showSuccess(`${input.poNumber ?? 'Reservation'} scheduled across ${pluralWeeks(order.length)}`);
      onSaved();
    } finally {
      saving.value = false;
      // Any week that did write changes what the cached PO read reports, so a halt after a
      // partial save has to invalidate it just as a full success does.
      if (Object.values(saveStates.value).some((state) => state === 'saved')) calendarStore.invalidatePurchaseOrders();
    }
  }

  async function save(): Promise<void> {
    if (locked.value) return;
    const order = evaluation.value.plannedWeekKeys;
    if (order.length === 0) return;
    frozenWeeks.value = weeks.value;
    saveStates.value = Object.fromEntries(order.map((weekKey) => [weekKey, 'waiting' as SaveState]));
    await runFrom(0);
  }

  async function retry(): Promise<void> {
    if (saving.value || !halted.value) return;
    const order = evaluation.value.plannedWeekKeys;
    const failedIndex = order.findIndex((weekKey) => saveStates.value[weekKey] === 'failed');
    if (failedIndex < 0) return;
    await runFrom(failedIndex);
  }

  function reset(): void {
    preset.value = 'fill';
    evenN.value = Math.max(1, Math.min(DEFAULT_EVEN_WEEKS, maxEven.value));
    cells.value = {};
    edited.value = {};
    extraWeeks.value = 0;
    saving.value = false;
    halted.value = false;
    saveStates.value = {};
    frozenWeeks.value = null;
  }

  return {
    preset,
    evenN,
    cells,
    saving,
    halted,
    locked,
    evaluation,
    cards,
    fillLine,
    evenLine,
    footer,
    problem,
    placedTotal,
    maxEven,
    applyPreset,
    setEvenN,
    setCell,
    addWeek,
    fillOpen,
    showMoreWeeks,
    save,
    retry,
    reset,
  };
}
